<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';

import type { IAffiliateProgramCommissionFilterItemAttributePayload } from '@modules/affiliate-program-commissions/submodules/edit/types';
import type {
  VFormFilterButtonWithForm,
  VFormFilterButtonWithFormProps,
} from '@modules/form-widgets/components/VFormButtonsWithFormGroupWidget/types';
import type { FormSchema } from '@modules/forms/components/VForm/types';
import { TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS } from '@modules/affiliate-program-commissions/constants';
import { filterOutAttributeSchema } from '@modules/affiliate-program-commissions/submodules/edit/utils';
import { useDevice } from '@modules/core/composables/device';
import VFormButtonsWithFormGroupWidget from '@modules/form-widgets/components/VFormButtonsWithFormGroupWidget/VFormButtonsWithFormGroupWidget.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { isNumber } from '@utils/guards';
import { watchImmediate } from '@vueuse/core';

import type {
  CommissionDealsEmits,
  CommissionDealsProps,
} from './types';

const props = withDefaults(defineProps<CommissionDealsProps>(), {});
const emit = defineEmits<CommissionDealsEmits>();

const { getTranslation } = useI18n();
const isGreaterThenSm = useDevice().breakpoints.sm;

const tempDeals = ref<IAffiliateProgramCommissionFilterItemAttributePayload[]>([]);
const tempAddDeal = ref<IAffiliateProgramCommissionFilterItemAttributePayload>();

watchImmediate(() => props.items, (newValue) => {
  tempDeals.value = newValue.map((item) => item.value);
  tempAddDeal.value = undefined;
}, { deep: true });

function getObtainedResults(excludeIndex: number): string[] {
  return props.items.map((item, index) => (
    index !== excludeIndex ? item.value.obtained_result : ''
  ));
}

const dealsFormSchema = computed<FormSchema<IAffiliateProgramCommissionFilterItemAttributePayload>>(() => ({
  obtained_result: {
    type: EFormInputTypes.SELECT,
    options: props.obtainedResultOptions,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'SET_STEP_ITEM_FORM_OBTAINED_RESULT').value,
    required: true,
  },
  condition_for_comparison: {
    type: EFormInputTypes.SELECT,
    options: props.comparisonOptions,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'SET_STEP_ITEM_MAIN_TITLE_EDIT_CONDITION_FOR_COMPARISON').value,
    required: true,
  },
  desired_value: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'SET_STEP_ITEM_MAIN_TITLE_EDIT_DESIRED_VALUE').value,
    required: true,
  },
}));

const buttons = computed<VFormFilterButtonWithForm<IAffiliateProgramCommissionFilterItemAttributePayload>[]>(() => (
  props.items.map((item, index) => {
    const formSchema = {
      ...dealsFormSchema.value,
      obtained_result: {
        ...dealsFormSchema.value.obtained_result,
        options: props.obtainedResultOptions.filter((option) => !getObtainedResults(index).includes(option.value)),
      },
    };

    return {
      formSchema: filterOutAttributeSchema(formSchema, index, tempDeals.value),
      value: item.value,
      type: 'secondary',
      allowRemove: true,
    } as VFormFilterButtonWithForm<IAffiliateProgramCommissionFilterItemAttributePayload>;
  })
));

const addButton = computed<VFormFilterButtonWithFormProps<IAffiliateProgramCommissionFilterItemAttributePayload>>(() => {
  const formSchema = {
    ...dealsFormSchema.value,
    obtained_result: {
      ...dealsFormSchema.value.obtained_result,
      options: props.obtainedResultOptions.filter((option) => !getObtainedResults(-1).includes(option.value)),
    },
  };
  if (tempAddDeal.value?.condition_for_comparison === 'any' && formSchema.desired_value) {
    delete formSchema.desired_value;
  }

  return {
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'CONDITION_ITEMS_CPA_ADD_CONDITION').value,
    formSchema,
  };
});

function onChange(index: number, value: IAffiliateProgramCommissionFilterItemAttributePayload): void {
  const button = props.items[index];

  if (button) {
    emit('change', button.index, value);
  }
}

function onInput(index: number | string, value: IAffiliateProgramCommissionFilterItemAttributePayload): void {
  if (isNumber(index)) {
    tempDeals.value[index] = value;
  }

  if (index === 'add') {
    tempAddDeal.value = value;
  }
}

function onRemove(index: number): void {
  const button = props.items[index];

  if (button) {
    emit('remove', button.index);
  }
}
</script>

<template>
  <VFormButtonsWithFormGroupWidget v-data-qa-auto-id="'CommissionDeals'"
    :buttons="buttons"
    :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'CONDITION_ITEMS_CPA_TITLE')"
    :add-button="addButton"
    :form-title="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'CONDITION_ITEMS_CPA_ADD_CONDITION')"
    :form-submit-btn="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'SET_STEP_ITEM_SAVE')"
    :form-close-btn="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'SET_STEP_ITEM_CANCEL')"
    :errors="errors"
    :readonly="readonly"
    :flex-direction-column="!isGreaterThenSm"
    @change="onChange"
    @remove="onRemove"
    @add="emit('add', $event)"
    @input="onInput"
  />
</template>
